import { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import QrCode2Rounded from '@mui/icons-material/QrCode2Rounded';
import { useParams } from 'react-router';
import { ApiError } from '@/api';
import { useAsync } from '@/hooks/useAsync';
import { AuthHeader } from '@/pages/auth/AuthHeader';
import { getSimulatedInvoice, paySimulatedInvoice } from '@/platform/billing/api';
import { formatDueDate, formatMoney } from '@/platform/billing/labels';

const PAID = new Set(['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH']);

/**
 * Simulated mode (the API has no ASAAS_API_KEY): stands in for Asaas's invoice page, with a
 * button that confirms the payment the same way Asaas's webhook would.
 */
export function SimulatedPaymentPage() {
  const { paymentId = '' } = useParams();
  const invoice = useAsync((signal) => getSimulatedInvoice(paymentId, signal), [paymentId]);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const data = invoice.data;
  const paid = data ? PAID.has(data.status) : false;

  const pay = async () => {
    setPaying(true);
    setError(null);
    try {
      const updated = await paySimulatedInvoice(paymentId);
      invoice.setData(() => updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível pagar.');
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      <AuthHeader title="Fatura" subtitle="Ambiente de testes: nenhum dinheiro de verdade." />
      <Chip size="small" variant="soft" color="warning" label="Pagamento simulado" sx={{ mb: 2 }} />
      {invoice.error ? (
        <Alert severity="error">
          Fatura não encontrada. (Com o Asaas configurado, as faturas abrem no site do Asaas.)
        </Alert>
      ) : !data ? (
        <CircularProgress />
      ) : (
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Typography variant="body2" color="text.secondary">
              {data.institution}
            </Typography>
            <Typography variant="body2">{data.description}</Typography>
          </Box>
          <Divider />
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography color="text.secondary">Vencimento {formatDueDate(data.dueDate)}</Typography>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 700 }}>
              {formatMoney(data.amountCents)}
            </Typography>
          </Stack>
          {paid ? (
            <Alert severity="success" icon={<CheckCircleRounded />}>
              <AlertTitle>Pagamento confirmado</AlertTitle>
              Pode fechar esta aba e voltar ao Lousa.
            </Alert>
          ) : (
            <Button
              variant="contained"
              size="large"
              startIcon={!paying && <QrCode2Rounded />}
              onClick={() => void pay()}
              disabled={paying}
            >
              {paying ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Simular pagamento por PIX'
              )}
            </Button>
          )}
        </Stack>
      )}
    </>
  );
}
